import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

function RoleTile({ value, title, desc, testid, current }) {
    const active = current === value;
    return (
        <label className={`relative flex flex-col gap-1 p-3 rounded-xl border-2 cursor-pointer transition-all ${
            active ? "border-primary bg-primary/5 shadow-sm" : "border-stone-200 hover:border-stone-300"
        }`}>
            <RadioGroupItem value={value} data-testid={testid} className="absolute right-2 top-2" />
            <span className="text-sm font-bold pr-6">{title}</span>
            <span className="text-[11px] text-muted-foreground leading-tight">{desc}</span>
        </label>
    );
}

export default function Auth() {
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    // `next` (URL query) wins over route state so `/auth?next=/claim/…` round-trips cleanly.
    const redirect = searchParams.get("next") || location.state?.from || "/";
    const initialCode = searchParams.get("code") || "";
    // Arriving from a pin-claim link — that link is the owner's invite.
    const claimMatch = redirect.match(/^\/claim\/([\w-]+)\?code=([^&]+)/);
    const claim = claimMatch ? { claim_listing_id: claimMatch[1], claim_code: decodeURIComponent(claimMatch[2]) } : null;
    const initialTab = searchParams.get("tab") === "register" || initialCode || claim ? "register" : "login";

    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [regForm, setRegForm] = useState({ name: "", email: "", password: "", role: "explorer", invite_code: initialCode });
    const [busy, setBusy] = useState(false);
    const [inviteOnly, setInviteOnly] = useState(false);
    const [inviteFeedback, setInviteFeedback] = useState(null);

    useEffect(() => {
        // Ask the backend whether invite-only mode is on so we can require the code.
        api.get("/invites/status")
            .then((r) => setInviteOnly(!!r.data?.invite_only))
            .catch(() => {});
    }, []);

    useEffect(() => {
        // Live-validate the invite code as the user types (debounced).
        const code = regForm.invite_code?.trim();
        if (!inviteOnly || !code) { setInviteFeedback(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/invites/verify", { code });
                setInviteFeedback({ ok: true, msg: `Valid · ${data.remaining} left${data.label ? ` · ${data.label}` : ""}` });
            } catch (e) {
                setInviteFeedback({ ok: false, msg: apiError(e) });
            }
        }, 400);
        return () => clearTimeout(t);
    }, [regForm.invite_code, inviteOnly]);

    const onLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(loginForm.email, loginForm.password);
            toast.success("Welcome back");
            navigate(redirect);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const onRegister = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await register(claim && !regForm.invite_code ? { ...regForm, ...claim } : regForm);
            toast.success("Account created");
            navigate(redirect);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const onGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-background to-stone-100 -z-10" />
            <div className="max-w-md w-full fade-up">
                <Link to="/" className="flex items-center gap-2 mb-8 group">
                    <span className="text-primary transition-transform duration-200 group-hover:-translate-y-0.5">
                        <svg width="32" height="40" viewBox="0 0 32 40" fill="none" aria-hidden="true">
                            <path d="M16 39.5 C 6 27 2 22 2 14 A 14 14 0 0 1 30 14 C 30 22 26 27 16 39.5 Z" fill="currentColor" stroke="currentColor" strokeWidth="0.5" strokeLinejoin="round"/>
                            <circle cx="16" cy="14" r="8.5" fill="#f7f3ea"/>
                            <g stroke="hsl(131 22% 29%)" strokeWidth="1.1" fill="none" strokeLinecap="round">
                                <ellipse cx="16" cy="14" rx="8.5" ry="8.5"/>
                                <ellipse cx="16" cy="14" rx="3.5" ry="8.5"/>
                                <path d="M7.5 14 h17"/><path d="M9 9.5 h14"/><path d="M9 18.5 h14"/>
                            </g>
                        </svg>
                    </span>
                    <span className="font-display text-2xl font-black">From Here</span>
                </Link>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                    Welcome to the neighbourhood
                </h1>
                <p className="text-muted-foreground mt-2">Sign in or create an account to buy, book, and sell locally.</p>

                <Tabs defaultValue={initialTab} className="mt-8">
                    <TabsList className="grid grid-cols-2 rounded-full bg-secondary w-full">
                        <TabsTrigger value="login" className="rounded-full" data-testid="tab-login">Sign in</TabsTrigger>
                        <TabsTrigger value="register" className="rounded-full" data-testid="tab-register">Create account</TabsTrigger>
                    </TabsList>

                    <TabsContent value="login" className="mt-6">
                        <form onSubmit={onLogin} className="space-y-4">
                            <div>
                                <Label htmlFor="login-email">Email</Label>
                                <Input id="login-email" data-testid="login-email" type="email" required
                                    value={loginForm.email}
                                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                            </div>
                            <div>
                                <Label htmlFor="login-password">Password</Label>
                                <Input id="login-password" data-testid="login-password" type="password" required
                                    value={loginForm.password}
                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                                <div className="mt-1.5 text-right">
                                    <Link to="/auth/forgot" data-testid="forgot-link" className="inline-block py-2 text-xs font-semibold text-muted-foreground hover:text-primary hover:underline">
                                        Forgot password?
                                    </Link>
                                </div>
                            </div>
                            <Button type="submit" disabled={busy} data-testid="login-submit"
                                className="w-full rounded-full bg-primary hover:bg-primary/90 hover:text-white">
                                {busy ? "Signing in…" : "Sign in"}
                            </Button>
                        </form>
                    </TabsContent>

                    <TabsContent value="register" className="mt-6">
                        <form onSubmit={onRegister} className="space-y-4">
                            <div>
                                <Label htmlFor="reg-name">Name</Label>
                                <Input id="reg-name" data-testid="reg-name" required
                                    value={regForm.name}
                                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
                            </div>
                            <div>
                                <Label htmlFor="reg-email">Email</Label>
                                <Input id="reg-email" data-testid="reg-email" type="email" required
                                    value={regForm.email}
                                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                            </div>
                            <div>
                                <Label htmlFor="reg-password">Password</Label>
                                <Input id="reg-password" data-testid="reg-password" type="password" required minLength={6}
                                    value={regForm.password}
                                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
                            </div>
                            <div>
                                <Label>I&apos;m joining as</Label>
                                <p className="text-xs text-muted-foreground mt-1 mb-2">
                                    Everyone&apos;s welcome — whether you&apos;re shopping or just exploring what&apos;s on.
                                </p>
                                <RadioGroup
                                    value={regForm.role}
                                    onValueChange={(v) => setRegForm({ ...regForm, role: v })}
                                    className="grid grid-cols-2 gap-2 mt-2"
                                >
                                    <RoleTile value="explorer" title="Explorer / Local" desc="Discover, follow, save" testid="role-explorer" current={regForm.role} />
                                    <RoleTile value="creator" title="Creator / Maker" desc="Artist, crafter, photographer" testid="role-creator" current={regForm.role} />
                                    <RoleTile value="space" title="Space & Hub" desc="Cafe, pub, venue, restaurant" testid="role-space" current={regForm.role} />
                                    <RoleTile value="performer" title="Performer / Musician" desc="Gigs, sets, shows" testid="role-performer" current={regForm.role} />
                                </RadioGroup>
                            </div>
                            {claim && (
                                <p data-testid="reg-claim-note" className="text-sm p-3 rounded-xl bg-primary/5 border border-primary/20">
                                    You're signing up to claim your pin — no invite code needed. We'll take you straight back to claim it.
                                </p>
                            )}
                            {!claim && (inviteOnly || regForm.invite_code) && (
                                <div>
                                    <Label htmlFor="reg-invite">Invite code {inviteOnly && <span className="text-destructive">*</span>}</Label>
                                    <Input id="reg-invite" data-testid="reg-invite-code"
                                        required={inviteOnly}
                                        value={regForm.invite_code}
                                        onChange={(e) => setRegForm({ ...regForm, invite_code: e.target.value.toUpperCase() })}
                                        placeholder="e.g. FROMHERE-XXXXXX"
                                        className={inviteFeedback ? (inviteFeedback.ok ? "border-primary" : "border-destructive") : ""} />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        No invite code? <Link to="/join" className="underline font-bold" data-testid="reg-get-early-access">Get early access</Link>
                                    </p>
                                    {inviteFeedback && (
                                        <p data-testid="invite-feedback"
                                            className={`text-xs mt-1 ${inviteFeedback.ok ? "text-primary" : "text-destructive"}`}>
                                            {inviteFeedback.msg}
                                        </p>
                                    )}
                                    {!inviteFeedback && inviteOnly && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            From Here is currently invite-only. Your code came in your invite email, from a friend, or on a flyer.
                                        </p>
                                    )}
                                </div>
                            )}
                            <Button type="submit" disabled={busy} data-testid="register-submit"
                                className="w-full rounded-full bg-primary hover:bg-primary/90 hover:text-white">
                                {busy ? "Creating…" : "Create account"}
                            </Button>
                        </form>
                    </TabsContent>
                </Tabs>

                <div className="flex items-center gap-3 my-6">
                    <div className="h-px flex-1 bg-stone-200" />
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">or</span>
                    <div className="h-px flex-1 bg-stone-200" />
                </div>

                <Button
                    type="button"
                    variant="outline"
                    data-testid="google-signin-btn"
                    onClick={onGoogle}
                    className="w-full rounded-full border-stone-300 hover:-translate-y-0.5 transition-transform"
                >
                    <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2">
                        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.5 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
                        <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.3 19 12.5 24 12.5c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.5 29 4.5 24 4.5c-7.5 0-13.9 4.2-17.7 10.6z"/>
                        <path fill="#4CAF50" d="M24 43.5c4.9 0 9.4-1.9 12.7-4.9l-6.1-5.2c-2 1.4-4.5 2.1-7.6 2.1-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C8.3 39.4 15.5 43.5 24 43.5z"/>
                        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.1 5.2c-.4.4 6.5-4.8 6.5-14.8 0-1.2-.1-2.4-.5-3.5z"/>
                    </svg>
                    Continue with Google
                </Button>
            </div>
        </div>
    );
}
